import type { Job } from "@agency/core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Bot, Hand, Monitor, XCircle } from "lucide-react";
import { useState } from "react";
import { callAction } from "../api";
import { useToast } from "./Toast";
import { Badge, Button, Card, CardHeader, cn } from "./ui";
import { useLocale, useT } from "../i18n";

/**
 * The Faraday executor job behind a record: where the bot is, what it logged, and the one thing it needs from
 * a person when it stopped. MFA / CAPTCHA / credential stoppages only offer "open on my PC"; the app never
 * collects those answers.
 */
export function JobCard({ job, invalidate }: { job: Job; invalidate: unknown[] }) {
  const t = useT();
  const { locale } = useLocale();
  const qc = useQueryClient();
  const { toast } = useToast();
  const [free, setFree] = useState("");
  const open = job.stoppages.find((s) => !s.answeredAt);
  const answer = useMutation({
    mutationFn: (v: { answer: string; runner?: "headless" | "client_machine" | "any" }) => callAction<"agency.job.answer", Job>("agency.job.answer", { jobId: job.id, stoppageId: open!.id, ...v }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: invalidate }); qc.invalidateQueries({ queryKey: ["today"] }); toast({ title: t("job.resumed") }); },
    onError: (e) => toast({ title: t("common.error"), body: (e as Error).message, tone: "error" }),
  });
  const cancel = useMutation({
    mutationFn: () => callAction<"agency.job.cancel", Job>("agency.job.cancel", { jobId: job.id }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: invalidate }); toast({ title: t("job.cancelled") }); },
    onError: (e) => toast({ title: t("common.error"), body: (e as Error).message, tone: "error" }),
  });

  const tone = job.status === "needs_human" ? "amber" : job.status === "failed" || job.status === "cancelled" ? "red" : job.status === "completed" ? "green" : "blue";
  const waiting = job.status === "queued" && !job.claimedBy;
  const tail = job.log.slice(-4);
  const prompt = open ? (locale === "es" && open.promptEs ? open.promptEs : open.prompt) : null;
  const machineOnly = open?.handoff === "client_machine";

  return (
    <Card className={cn(job.status === "needs_human" && "ring-2 ring-accent")}>
      <CardHeader
        title={<span className="inline-flex items-center gap-2"><Bot className="h-4 w-4" />{t("job.title")}</span>}
        action={<Badge tone={tone}>{t(`job.status.${job.status}`)}</Badge>}
      />
      <div className="px-5 pb-5 space-y-3 text-sm">
        <p className="text-ink/60 text-xs">
          {waiting ? t("job.waitingExecutor") : job.claimedBy ? `${t("job.runningOn")} ${job.claimedBy}` : null}
          {job.attempts > 1 ? ` · ${t("job.attempt")} ${job.attempts}/${job.maxAttempts}` : null}
        </p>

        {open && prompt ? (
          <div className="rounded-2xl bg-accent/15 p-4 space-y-3">
            <p className="font-bold inline-flex items-center gap-2 text-amber-900"><Hand className="h-4 w-4" />{t("job.needsYou")}</p>
            <p className="text-ink">{prompt}</p>
            {machineOnly ? (
              <>
                <p className="text-xs text-ink/60">{t("job.machineOnly")}</p>
                <Button onClick={() => answer.mutate({ answer: "continue_on_my_pc", runner: "client_machine" })} loading={answer.isPending}><Monitor className="h-4 w-4" />{t("job.openOnMyPc")}</Button>
              </>
            ) : open.options?.length ? (
              <div className="flex flex-wrap gap-2">
                {open.options.map((o) => (
                  <Button key={o} variant="outline" onClick={() => answer.mutate({ answer: o })} loading={answer.isPending}>{o}</Button>
                ))}
              </div>
            ) : (
              <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); if (free.trim()) answer.mutate({ answer: free.trim() }); }}>
                <input className="flex-1 rounded-xl border border-ink/15 px-3 py-2" value={free} onChange={(e) => setFree(e.target.value)} placeholder={t("job.answerPlaceholder")} />
                <Button type="submit" loading={answer.isPending}>{t("job.send")}</Button>
              </form>
            )}
            {!machineOnly ? (
              <button type="button" className="text-xs text-ink/55 underline inline-flex items-center gap-1" onClick={() => answer.mutate({ answer: "continue_on_my_pc", runner: "client_machine" })}>
                <Monitor className="h-3.5 w-3.5" />{t("job.ratherOnMyPc")}
              </button>
            ) : null}
          </div>
        ) : null}

        {tail.length ? (
          <ol className="space-y-1 text-xs text-ink/70">
            {tail.map((l, i) => (
              <li key={`${l.at}-${i}`} className="flex gap-2">
                <span className="tabular-nums text-ink/40 shrink-0">{new Date(l.at).toLocaleTimeString(locale === "es" ? "es-MX" : "en-US", { hour: "numeric", minute: "2-digit", second: "2-digit" })}</span>
                <span className="truncate">{l.message}</span>
              </li>
            ))}
          </ol>
        ) : null}

        {job.error && (job.status === "failed" || job.status === "queued") ? <p className="text-xs text-red-700">{job.error}</p> : null}

        {job.status !== "completed" && job.status !== "failed" && job.status !== "cancelled" ? (
          <button type="button" className="text-xs text-ink/50 underline inline-flex items-center gap-1" onClick={() => cancel.mutate()} disabled={cancel.isPending}>
            <XCircle className="h-3.5 w-3.5" />{t("job.cancel")}
          </button>
        ) : null}
      </div>
    </Card>
  );
}
